import React, { useEffect, useRef, useState } from "react";
import {
  downloadCsv,
  getSampleCsvContent,
  guestsToCsv,
  hasExportableGuests,
  parseCsvToGuests,
} from "../../utils/inviteflowGuestCsv";

const emptyGuest = () => ({ name: "", email: "", phone: "" });

const GuestSubmitForm = ({
  onSubmit,
  loading,
  error,
  title = "Enter Guests",
  phoneRequired = false,
  addButtonLabel = "+ Add Guest",
  resetSignal = 0,
  availableSeats,
}) => {
  const [guests, setGuests] = useState([emptyGuest()]);
  const [importMessage, setImportMessage] = useState("");
  const [importErrors, setImportErrors] = useState([]);
  const [seatWarning, setSeatWarning] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    setGuests([emptyGuest()]);
    setImportMessage("");
    setImportErrors([]);
    setSeatWarning("");
  }, [resetSignal]);

  const updateGuest = (index, field, value) => {
    setGuests((prev) =>
      prev.map((g, i) => (i === index ? { ...g, [field]: value } : g))
    );
  };

  const addGuest = () => setGuests((prev) => [...prev, emptyGuest()]);

  const removeGuest = (index) =>
    setGuests((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(guests);
  };

  const handleDownloadSample = () => {
    downloadCsv("inviteflow-guests-sample.csv", getSampleCsvContent());
  };

  const handleExport = () => {
    downloadCsv("inviteflow-guests.csv", guestsToCsv(guests));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setImportMessage("");
    setImportErrors([]);
    setSeatWarning("");

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text !== "string") {
        setImportErrors(["Could not read file."]);
        return;
      }

      const { guests: parsedGuests, errors, skippedCount } = parseCsvToGuests(text);

      if (parsedGuests.length > 0) {
        setGuests((prev) => {
          const next = [...prev, ...parsedGuests];
          if (availableSeats != null && next.length > availableSeats) {
            setSeatWarning(
              `You have ${next.length} guests but only ${availableSeats} seat${availableSeats !== 1 ? "s" : ""} available. Submission may fail.`
            );
          }
          return next;
        });
      }

      if (errors.length > 0) {
        setImportErrors(errors);
      }

      if (parsedGuests.length > 0) {
        const parts = [`Added ${parsedGuests.length} guest${parsedGuests.length > 1 ? "s" : ""} from CSV.`];
        if (skippedCount > 0) {
          parts.push(`${skippedCount} empty row${skippedCount > 1 ? "s" : ""} skipped.`);
        }
        setImportMessage(parts.join(" "));
      } else if (errors.length === 0) {
        setImportErrors(["No valid guest rows found in CSV."]);
      }
    };

    reader.onerror = () => {
      setImportErrors(["Failed to read the CSV file."]);
    };

    reader.readAsText(file, "UTF-8");
  };

  const canExport = hasExportableGuests(guests);

  return (
    <form className="inviteflow-guest-form" onSubmit={handleSubmit}>
      <h3 className="inviteflow-guest-form__title">{title}</h3>

      <div className="inviteflow-guest-form__toolbar">
        <button
          type="button"
          className="inviteflow-btn inviteflow-btn--secondary"
          onClick={handleDownloadSample}
        >
          Download Sample CSV
        </button>
        <button
          type="button"
          className="inviteflow-btn inviteflow-btn--secondary"
          onClick={handleUploadClick}
        >
          Upload CSV
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="inviteflow-guest-form__file-input"
          onChange={handleFileChange}
          aria-hidden="true"
          tabIndex={-1}
        />
        <button
          type="button"
          className="inviteflow-btn inviteflow-btn--secondary"
          onClick={handleExport}
          disabled={!canExport}
        >
          Export CSV
        </button>
      </div>

      {importMessage && (
        <p className="inviteflow-guest-form__import-message">{importMessage}</p>
      )}
      {importErrors.length > 0 && (
        <div className="inviteflow-guest-form__import-error">
          <p>CSV import issues:</p>
          <ul>
            {importErrors.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </div>
      )}
      {seatWarning && (
        <p className="inviteflow-guest-form__import-warning">{seatWarning}</p>
      )}

      {error && <p className="inviteflow-guest-form__error">{error}</p>}

      {guests.map((guest, idx) => (
        <div key={idx} className="inviteflow-guest-form__row">
          <span className="inviteflow-guest-form__index">#{idx + 1}</span>
          <input
            type="text"
            placeholder="Full Name"
            value={guest.name}
            onChange={(e) => updateGuest(idx, "name", e.target.value)}
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={guest.email}
            onChange={(e) => updateGuest(idx, "email", e.target.value)}
            required
          />
          <input
            type="tel"
            placeholder={phoneRequired ? "Phone" : "Phone (optional)"}
            value={guest.phone}
            onChange={(e) => updateGuest(idx, "phone", e.target.value)}
            required={phoneRequired}
          />
          {guests.length > 1 && (
            <button
              type="button"
              className="inviteflow-btn inviteflow-btn--remove"
              onClick={() => removeGuest(idx)}
            >
              ✕
            </button>
          )}
        </div>
      ))}

      <div className="inviteflow-guest-form__footer">
        <button
          type="button"
          className="inviteflow-btn inviteflow-btn--secondary"
          onClick={addGuest}
        >
          {addButtonLabel}
        </button>
        <button
          type="submit"
          className="inviteflow-btn inviteflow-btn--primary"
          disabled={loading}
        >
          {loading ? "Submitting…" : `Submit ${guests.length} Guest${guests.length > 1 ? "s" : ""}`}
        </button>
      </div>
    </form>
  );
};

export default GuestSubmitForm;
